// In-browser simulator — produces the same ClusterSnapshot shape as the
// FastAPI backend, so the UI can run fully standalone on Cloudflare Pages.
// Logic is ported from backend/app/collectors/mock.py (which itself ports sim.js).

import type { Alert, ClusterSnapshot, GpuSeries, IbPort, Job, NodeState, Partition } from "../types";

const NODES = ["hpc-node-01", "hpc-node-02", "hpc-node-03", "hpc-node-04"];
const GPUS_PER_NODE = 2;
const HISTORY = 180;
const TICK_MS = 1000;

function rand(a: number, b: number) { return a + Math.random() * (b - a); }
function clamp(v: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, v)); }
function smooth(prev: number, target: number, k = 0.12) { return prev + (target - prev) * k; }
const nowMs = () => Date.now();

class Ring {
  readonly cap: number;
  private buf: number[];
  constructor(cap: number, fill = 0) {
    this.cap = cap;
    this.buf = new Array(cap).fill(fill);
  }
  push(v: number) { this.buf.shift(); this.buf.push(v); }
  last() { return this.buf[this.buf.length - 1]; }
  data() { return this.buf.slice(); }
  replaceAll(v: number) { this.buf = new Array(this.cap).fill(v); }
}

interface GpuState {
  id: string; node: string; gpu_id: number;
  phase: number; cyclePeriod: number;
  loadHigh: number; loadLow: number; dutyCycle: number;
  memTotal: number;
  util: Ring; mem: Ring; temp: Ring; power: Ring; nvlink: Ring;
  xidCount: number; eccSbe: number; eccDbe: number;
  lastXidCode: number | null; lastXidAt: number;
  status: "running" | "idle" | "xid-faulted" | "down";
}

// ---- Vera Rubin NVL144 per-package specs (publicly announced GTC 2025) ----
// Each simulated "GPU" entity represents one Rubin *package* (2 dies each).
// HBM4: 288 GB per package (8× 36 GB HBM4 stacks)
// TDP:  ~2400 W per package (die ≈ 1200 W × 2)
// NVLink 6: 3.6 TB/s bidirectional per package
// C2C: 1.8 TB/s between Vera CPU and 2 Rubin packages
const HBM4_MB = 288 * 1024;          // 288 GB → 294912 MiB
const TDP_W = 2400;                  // package TDP watts
const NVLINK6_GBS = 3600;            // 3.6 TB/s
const IDLE_POWER_W = 110;            // Rubin idle floor is higher than Hopper

interface NodeStateInt {
  name: string;
  state: "idle" | "alloc" | "mix" | "drain" | "down";
  bootedAt: number;
  cpuUtil: Ring; memUtil: Ring; load1: Ring;
  diskR: Ring; diskW: Ring;
}

interface IbPortState {
  node: string; hca: string; port: number; speed: number;
  state: 0 | 1;
  xmit: Ring; recv: Ring; sym: Ring;
  xmitTotal: number; recvTotal: number; symTotal: number;
}

export class ClientSim {
  readonly historyPoints = HISTORY;
  readonly clusterName = "rubin-lab (demo)";
  readonly clusterRegion = "il-pages-1";
  private tickN = 0;
  private gpus: GpuState[] = [];
  private nodes: NodeStateInt[] = [];
  private ibPorts: IbPortState[] = [];
  private jobs: Job[] = [];
  private partitions: Partition[] = [];
  private alerts = new Map<string, Alert>();
  private alertCounter = 1;
  private scheduled: { at: number; fn: () => void }[] = [];
  private timer: number | null = null;
  private listeners = new Set<(s: ClusterSnapshot) => void>();

  constructor() {
    this.seed();
  }

  // ---- public API
  start(): void {
    if (this.timer !== null) return;
    this.step();
    this.timer = window.setInterval(() => this.step(), TICK_MS);
  }
  stop(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }
  subscribe(fn: (s: ClusterSnapshot) => void): () => void {
    this.listeners.add(fn);
    fn(this.snapshot());
    return () => { this.listeners.delete(fn); };
  }
  snapshot(): ClusterSnapshot {
    return {
      t: nowMs(),
      tick: this.tickN,
      cluster_name: this.clusterName,
      cluster_region: this.clusterRegion,
      history_points: HISTORY,
      nodes: this.nodes.map<NodeState>(n => ({
        name: n.name,
        state: n.state,
        sockets: 2, cores: 64, threads: 128, ram_gb: 512,
        booted_at: n.bootedAt,
        cpu_util: n.cpuUtil.data(),
        mem_util: n.memUtil.data(),
        load1: n.load1.data(),
        disk_read_mbs: n.diskR.data(),
        disk_write_mbs: n.diskW.data(),
      })),
      gpus: this.gpus.map<GpuSeries>(g => ({
        id: g.id, node: g.node, gpu_id: g.gpu_id, model: "Rubin-NVL144 (2-die pkg)",
        util: g.util.data(), mem_mb: g.mem.data(),
        temp_c: g.temp.data(), power_w: g.power.data(),
        nvlink_gbs: g.nvlink.data(),
        mem_total_mb: g.memTotal, status: g.status,
        xid_count: g.xidCount, ecc_sbe: g.eccSbe, ecc_dbe: g.eccDbe,
        last_xid_code: g.lastXidCode,
      })),
      ib_ports: this.ibPorts.map<IbPort>(p => ({
        node: p.node, hca: p.hca, port: p.port, speed_gb: p.speed,
        state: p.state,
        xmit_gbs: p.xmit.data(), recv_gbs: p.recv.data(),
        symbol_errors: p.sym.data(),
        xmit_bytes_total: p.xmitTotal, recv_bytes_total: p.recvTotal,
        symbol_errors_total: p.symTotal,
      })),
      jobs: this.jobs.slice(),
      partitions: this.partitions.slice(),
      alerts: [...this.alerts.values()].sort((a, b) => b.fired_at - a.fired_at),
    };
  }

  // ---- demo triggers
  triggerXid79(gi?: number) {
    const gp = this.gpus[gi ?? Math.floor(Math.random() * this.gpus.length)];
    gp.xidCount++;
    gp.lastXidAt = nowMs();
    gp.lastXidCode = 79;
    gp.status = "xid-faulted";
    this.fire({
      fingerprint: `xid-${gp.id}-${gp.xidCount}`,
      name: "XIDErrorDetected", severity: "critical", team: "hpc-ops",
      summary: `XID 79 (GPU fallen off the bus) on ${gp.id}`,
      description: "Fatal XID event: the GPU is no longer visible on the PCIe bus. Active workload is lost; node likely needs a reboot. This metric is non-recoverable from software.",
      labels: { node: gp.node, gpu: `gpu${gp.gpu_id}`, xid: "79" },
      runbook: "https://runbooks.rubin-hpc.int/xid-79-bus-fallen",
      value: "1",
    });
  }
  triggerNodeDown(name: string) {
    const nd = this.nodes.find(n => n.name === name);
    if (!nd) return;
    nd.state = "down";
    this.fire({
      fingerprint: `nodedown-${name}`,
      name: "NodeDown", severity: "critical", team: "hpc-ops",
      summary: `Node ${name} is unreachable`,
      description: "node-exporter has not responded for > 2m. Investigate out-of-band (BMC/iDRAC/iLO).",
      labels: { node: name },
      runbook: "https://runbooks.rubin-hpc.int/node-down",
      value: "0",
    });
  }
  triggerIbLinkDown(idx = 0) {
    const p = this.ibPorts[idx];
    if (!p) return;
    p.state = 0;
    this.fire({
      fingerprint: `iblink-${p.node}-${p.hca}-${p.port}`,
      name: "IBLinkDown", severity: "critical", team: "fabric",
      summary: `InfiniBand link down on ${p.node} ${p.hca}/port${p.port}`,
      description: "Port state transitioned to 0 (Down). Verify cable seating, port error counters, and SM logs for remote endpoint.",
      labels: { node: p.node, hca: p.hca, port: String(p.port) },
      runbook: "https://runbooks.rubin-hpc.int/ib-link-down",
      value: "0",
    });
  }
  triggerIbSymbolSpike(idx = 3) {
    const p = this.ibPorts[idx] ?? this.ibPorts[0];
    for (let i = 0; i < 5; i++) {
      this.scheduled.push({
        at: nowMs() + i * 1000,
        fn: () => {
          const spike = 40 + Math.floor(rand(0, 30));
          p.symTotal += spike;
          p.sym.push(spike);
        },
      });
    }
    this.fire({
      fingerprint: `ibsymspike-${p.node}-${p.hca}-${p.port}`,
      name: "IBSymbolErrorSpike", severity: "warning", team: "fabric",
      summary: `IB symbol error spike on ${p.node} ${p.hca}/port${p.port}`,
      description: ">100 symbol errors in 5m — a strong sign of a dirty connector or failing cable. If no self-recovery, mark fabric for maintenance window.",
      labels: { node: p.node, hca: p.hca, port: String(p.port) },
      runbook: "https://runbooks.rubin-hpc.int/ib-symbol-spike",
      value: "150",
    });
  }
  triggerGpuMemHigh(gi = 0) {
    const gp = this.gpus[gi % this.gpus.length];
    gp.mem.replaceAll(gp.memTotal * 0.95);
    this.fire({
      fingerprint: `gpumem-${gp.id}`,
      name: "GPUMemoryHigh", severity: "warning", team: "hpc-ops",
      summary: `GPU memory > 90% on ${gp.id}`,
      description: "Sustained high HBM utilization. Risk of OOM kill on the workload. Consider gradient checkpointing or smaller batch.",
      labels: { node: gp.node, gpu: `gpu${gp.gpu_id}` },
      runbook: "https://runbooks.rubin-hpc.int/gpu-mem-high",
      value: "94%",
    });
  }
  resolveAll() {
    const keep = new Map<string, Alert>();
    for (const [k, a] of this.alerts) if (a.severity === "info") keep.set(k, a);
    this.alerts = keep;
    for (const n of this.nodes) if (n.state === "down") n.state = "alloc";
    for (const g of this.gpus) if (g.status === "xid-faulted") g.status = "running";
    for (const p of this.ibPorts) if (p.state === 0) p.state = 1;
  }

  // ---- internals
  private fire(a: Omit<Alert, "id" | "fired_at" | "state"> & { firedAt?: number }) {
    if (this.alerts.has(a.fingerprint)) return;
    const alert: Alert = {
      id: this.alertCounter++,
      fired_at: a.firedAt ?? nowMs(),
      state: "firing",
      fingerprint: a.fingerprint,
      name: a.name,
      severity: a.severity,
      team: a.team,
      summary: a.summary,
      description: a.description,
      labels: a.labels,
      runbook: a.runbook ?? null,
      value: a.value ?? null,
    };
    this.alerts.set(a.fingerprint, alert);
  }
  private resolve(fp: string) { this.alerts.delete(fp); }

  private seed(): void {
    const now = nowMs();
    // GPUs (Rubin packages)
    NODES.forEach((node, ni) => {
      for (let g = 0; g < GPUS_PER_NODE; g++) {
        this.gpus.push({
          id: `${node}/gpu${g}`, node, gpu_id: g,
          phase: rand(0, Math.PI * 2),
          cyclePeriod: rand(80, 140),
          loadHigh: rand(78, 92),
          loadLow: rand(2, 8),
          dutyCycle: rand(0.65, 0.88),
          memTotal: HBM4_MB,
          util: new Ring(HISTORY, rand(5, 15)),
          // mem used during pretrain runs typically 80-90% of HBM → ~230 GB
          mem: new Ring(HISTORY, rand(20, 60) * 1024),
          temp: new Ring(HISTORY, rand(30, 38)),
          power: new Ring(HISTORY, rand(110, 180)),
          nvlink: new Ring(HISTORY, rand(20, 60)),
          xidCount: 0, eccSbe: 0, eccDbe: 0,
          lastXidCode: null, lastXidAt: 0,
          status: "running",
        });
        void ni;
      }
    });
    this.gpus[0].phase = 0;
    this.gpus[0].cyclePeriod = 120;
    this.gpus[0].dutyCycle = 0.82;

    // Nodes
    NODES.forEach(n => {
      this.nodes.push({
        name: n, state: "alloc",
        bootedAt: now - (3 * 86_400_000 + rand(0, 20 * 3_600_000)),
        cpuUtil: new Ring(HISTORY, rand(15, 30)),
        memUtil: new Ring(HISTORY, rand(25, 40)),
        load1: new Ring(HISTORY, rand(0.4, 1.2)),
        diskR: new Ring(HISTORY, rand(10, 30)),
        diskW: new Ring(HISTORY, rand(5, 15)),
      });
    });

    // ConnectX-9 ports — XDR 800 Gb/s on Quantum-X800 fabric
    NODES.forEach((n, i) => {
      for (let p = 1; p <= 2; p++) {
        this.ibPorts.push({
          node: n, hca: `mlx5_${i}`, port: p,
          speed: 800,                      // XDR 800 Gb/s
          state: 1,
          xmit: new Ring(HISTORY, rand(20, 60)),
          recv: new Ring(HISTORY, rand(20, 60)),
          sym: new Ring(HISTORY, 0),
          xmitTotal: rand(1e13, 9e13),
          recvTotal: rand(1e13, 9e13),
          symTotal: Math.floor(rand(0, 40)),
        });
      }
    });

    // Jobs — plausible agentic-inference / training mix on Rubin NVL144
    this.jobs = [
      { id: 142057, name: "llama4-405b-pretrain", user: "arielk", partition: "gpu-rubin-nvl",
        state: "RUNNING",
        nodes: ["hpc-node-01", "hpc-node-02"],
        gpus: ["hpc-node-01/gpu0","hpc-node-01/gpu1","hpc-node-02/gpu0","hpc-node-02/gpu1"],
        started_at: now - 2 * 3_600_000 - 48 * 60_000,
        elapsed_s: 0, wall_limit_s: 24 * 3600, qos: "priority",
        pending_since: null, reason: null },
      { id: 142058, name: "deepseek-v4-moe-ft", user: "mayag", partition: "gpu-rubin",
        state: "RUNNING",
        nodes: ["hpc-node-03"],
        gpus: ["hpc-node-03/gpu0","hpc-node-03/gpu1"],
        started_at: now - 58 * 60_000,
        elapsed_s: 0, wall_limit_s: 12 * 3600, qos: "normal",
        pending_since: null, reason: null },
      { id: 142061, name: "mdsim-amber22", user: "liorz", partition: "cpu-vera",
        state: "RUNNING",
        nodes: ["hpc-node-04"], gpus: [],
        started_at: now - 11 * 60_000,
        elapsed_s: 0, wall_limit_s: 48 * 3600, qos: "normal",
        pending_since: null, reason: null },
      { id: 142064, name: "agent-swarm-inference", user: "natalid", partition: "gpu-rubin",
        state: "PENDING", nodes: [], gpus: [],
        started_at: 0, elapsed_s: 0, wall_limit_s: 2 * 3600, qos: "normal",
        pending_since: now - 34 * 60_000, reason: "Resources" },
      { id: 142067, name: "genome-align-b37", user: "omerl", partition: "cpu-vera",
        state: "PENDING", nodes: [], gpus: [],
        started_at: 0, elapsed_s: 0, wall_limit_s: 6 * 3600, qos: "normal",
        pending_since: now - 9 * 60_000, reason: "Priority" },
      { id: 142068, name: "bert-ablation", user: "arielk", partition: "gpu-rubin-nvl",
        state: "PENDING", nodes: [], gpus: [],
        started_at: 0, elapsed_s: 0, wall_limit_s: 4 * 3600, qos: "normal",
        pending_since: now - 3 * 60_000, reason: "Priority" },
    ];

    // Partitions — Rubin-era names + Vera CPU partition
    this.partitions = [
      { name: "gpu-rubin", nodes: ["hpc-node-03"], total_gpus: 2, total_cpus: 88,
        idle: 0, alloc: 0, mix: 0, drain: 0, down: 0 },
      { name: "gpu-rubin-nvl", nodes: ["hpc-node-01", "hpc-node-02"], total_gpus: 4, total_cpus: 176,
        idle: 0, alloc: 0, mix: 0, drain: 0, down: 0 },
      { name: "cpu-vera", nodes: ["hpc-node-04"], total_gpus: 0, total_cpus: 88,
        idle: 0, alloc: 0, mix: 0, drain: 0, down: 0 },
    ];

    // Initial alerts
    this.fire({
      fingerprint: "xid-init", name: "XIDErrorDetected", severity: "critical", team: "hpc-ops",
      summary: "XID 31 (MMU fault) on hpc-node-02/gpu1",
      description: "GPU reported XID error indicating a potential memory addressing fault. Workload stability at risk.",
      labels: { node: "hpc-node-02", gpu: "gpu1", xid: "31" },
      runbook: "https://runbooks.rubin-hpc.int/xid-error", value: "1",
      firedAt: now - 4 * 60_000,
    });
    this.fire({
      fingerprint: "queuewait-init", name: "LongQueueWait", severity: "warning", team: "scheduler",
      summary: "Job 142064 pending > 30m in gpu-h100",
      description: "Job sd3-inference-bench (natalid) has been pending for 34 minutes, reason=Resources. Check partition saturation.",
      labels: { jobid: "142064", partition: "gpu-h100" },
      runbook: "https://runbooks.rubin-hpc.int/long-queue-wait", value: "34m",
      firedAt: now - 4 * 60_000,
    });
    this.fire({
      fingerprint: "ecc-init", name: "ECCSBEWarn", severity: "info", team: "hpc-ops",
      summary: "Rising ECC SBE rate on hpc-node-01/gpu0",
      description: "Single-bit ECC errors above baseline. Hardware ECC is correcting, no data loss; monitor for trend before scheduling maintenance.",
      labels: { node: "hpc-node-01", gpu: "gpu0" },
      runbook: "https://runbooks.rubin-hpc.int/ecc-sbe", value: "7",
      firedAt: now - 2 * 60_000,
    });
  }

  private step(): void {
    this.tickN++;
    const t = nowMs();

    // GPUs
    for (const gp of this.gpus) {
      const cycleT = (this.tickN + gp.phase * gp.cyclePeriod) % gp.cyclePeriod;
      const onPhase = cycleT < gp.cyclePeriod * gp.dutyCycle;
      const targetUtil = onPhase
        ? gp.loadHigh + Math.sin(this.tickN / 7 + gp.phase) * 4 + rand(-2, 2)
        : gp.loadLow + rand(-1, 3);
      const newUtil = clamp(smooth(gp.util.last(), targetUtil, 0.18), 0, 100);
      gp.util.push(newUtil);

      const targetTemp = 32 + (newUtil / 100) * 48 + Math.sin(this.tickN / 30 + gp.phase) * 1.5;
      gp.temp.push(smooth(gp.temp.last(), targetTemp, 0.08));

      // Rubin package: ~110 W idle → ~2400 W at 100% load (2× die × 1200 W)
      const targetPower = IDLE_POWER_W + (newUtil / 100) * (TDP_W - IDLE_POWER_W) + rand(-30, 30);
      gp.power.push(clamp(smooth(gp.power.last(), targetPower, 0.12), 90, TDP_W + 50));

      if (onPhase) {
        // pretrain memory footprint: 75-88 % of 288 GB HBM4 → ~220-250 GB
        const targetMem = 220 * 1024 + Math.sin(this.tickN / 40 + gp.phase) * 20 * 1024 + rand(-2000, 2000);
        gp.mem.push(clamp(smooth(gp.mem.last(), targetMem, 0.05), 0, gp.memTotal));
      } else {
        gp.mem.push(clamp(smooth(gp.mem.last(), 18 * 1024 + rand(-1500, 1500), 0.04), 0, gp.memTotal));
      }

      // NVLink 6: 3.6 TB/s = 3600 GB/s. Peak inter-GPU pretrain traffic ~70 %.
      const targetNvlink = (newUtil / 100) * (NVLINK6_GBS * 0.75) + rand(-50, 50);
      gp.nvlink.push(clamp(smooth(gp.nvlink.last(), Math.max(0, targetNvlink), 0.2), 0, NVLINK6_GBS));

      if (Math.random() < 0.003 && onPhase) { gp.eccSbe++; }
    }

    // High GPU temp alert
    for (const gp of this.gpus) {
      const fp = `hightemp-${gp.id}`;
      if (gp.temp.last() > 85) {
        this.fire({
          fingerprint: fp, name: "HighGPUTemperature", severity: "warning", team: "hpc-ops",
          summary: `GPU temperature > 85°C on ${gp.id}`,
          description: "Sustained thermal violation. Check cooling loop, fan speed, and neighboring hardware.",
          labels: { node: gp.node, gpu: `gpu${gp.gpu_id}` },
          runbook: "https://runbooks.rubin-hpc.int/high-gpu-temp",
          value: `${gp.temp.last().toFixed(1)}°C`,
        });
      } else if (gp.temp.last() < 82) {
        this.resolve(fp);
      }
    }

    // Nodes
    for (const nd of this.nodes) {
      const nodeGpus = this.gpus.filter(g => g.node === nd.name);
      const gpuLoad = nodeGpus.length ? nodeGpus.reduce((a, g) => a + g.util.last(), 0) / nodeGpus.length : 0;
      const targetCpu = 15 + (gpuLoad / 100) * 55 + Math.sin(this.tickN / 25) * 4 + rand(-3, 3);
      nd.cpuUtil.push(clamp(smooth(nd.cpuUtil.last(), targetCpu, 0.1), 0, 100));
      const targetMem = 30 + (gpuLoad / 100) * 45 + rand(-2, 2);
      nd.memUtil.push(clamp(smooth(nd.memUtil.last(), targetMem, 0.05), 0, 100));
      nd.load1.push(clamp(smooth(nd.load1.last(), (targetCpu / 100) * 1.6, 0.15), 0, 4));
      nd.diskR.push(clamp(smooth(nd.diskR.last(), 10 + rand(-4, 40), 0.3), 0, 200));
      nd.diskW.push(clamp(smooth(nd.diskW.last(), 5 + rand(-3, 18), 0.3), 0, 100));
    }

    // IB
    for (const p of this.ibPorts) {
      const node = this.nodes.find(n => n.name === p.node)!;
      const busy = node.cpuUtil.last() / 100;
      const targetXmit = busy * (p.speed / 8) * 0.7 + rand(-1, 1);
      p.xmit.push(clamp(smooth(p.xmit.last(), Math.max(0, targetXmit), 0.25), 0, p.speed / 8));
      const targetRecv = busy * (p.speed / 8) * 0.65 + rand(-1, 1);
      p.recv.push(clamp(smooth(p.recv.last(), Math.max(0, targetRecv), 0.25), 0, p.speed / 8));
      p.xmitTotal += p.xmit.last() * 1e9;
      p.recvTotal += p.recv.last() * 1e9;
      const se = Math.random() < 0.02 ? Math.floor(rand(1, 4)) : 0;
      p.sym.push(se);
      p.symTotal += se;
    }

    // Partitions
    for (const part of this.partitions) {
      const inPart = this.nodes.filter(n => part.nodes.includes(n.name));
      part.idle = inPart.filter(n => n.state === "idle").length;
      part.alloc = inPart.filter(n => n.state === "alloc").length;
      part.mix = inPart.filter(n => n.state === "mix").length;
      part.drain = inPart.filter(n => n.state === "drain").length;
      part.down = inPart.filter(n => n.state === "down").length;
    }

    // Partition full alerts
    for (const part of this.partitions) {
      const fp = `partfull-${part.name}`;
      const total = part.nodes.length;
      if (total > 0 && part.alloc === total && part.drain === 0) {
        this.fire({
          fingerprint: fp, name: "SlurmPartitionFullyAllocated", severity: "info", team: "scheduler",
          summary: `Partition ${part.name} is fully allocated`,
          description: "All nodes in partition are in alloc state. New jobs will queue.",
          labels: { partition: part.name },
          runbook: "https://runbooks.rubin-hpc.int/partition-full",
          value: "100%",
        });
      } else {
        this.resolve(fp);
      }
    }

    // Jobs elapsed
    for (const j of this.jobs) {
      if (j.state === "RUNNING") j.elapsed_s = (t - j.started_at) / 1000;
    }

    // Scheduled events
    this.scheduled = this.scheduled.filter(s => {
      if (s.at <= t) { s.fn(); return false; }
      return true;
    });

    // Broadcast
    const snap = this.snapshot();
    for (const fn of this.listeners) fn(snap);
  }
}

// singleton so React strict-mode double-mount doesn't start two timers
let _instance: ClientSim | null = null;
export function getClientSim(): ClientSim {
  if (!_instance) {
    _instance = new ClientSim();
    _instance.start();
  }
  return _instance;
}
